import { useEffect, useState } from "react";
import { ShieldCheck, ShieldOff, KeyRound, Copy, Check } from "lucide-react";
import StaffLayout from "@/components/staff/StaffLayout";
import { api } from "@/lib/api";
import { toast } from "sonner";

export default function StaffSecurityPage() {
  const [status, setStatus] = useState({ enabled: false });
  const [setup, setSetup] = useState(null);
  const [code, setCode] = useState("");
  const [copied, setCopied] = useState(false);

  function refresh() {
    api.get("/auth/2fa/status").then((r) => setStatus(r.data)).catch(() => {});
  }
  useEffect(refresh, []);

  async function startSetup() {
    try {
      const r = await api.post("/auth/2fa/setup");
      setSetup(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Setup failed");
    }
  }

  async function verifyCode(e) {
    e.preventDefault();
    try {
      await api.post("/auth/2fa/verify", { code });
      toast.success("Two-factor authentication enabled");
      setSetup(null); setCode("");
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Invalid code");
    }
  }

  async function disable() {
    if (!confirm("Disable two-factor authentication? Your account will be less secure.")) return;
    await api.post("/auth/2fa/disable");
    toast.success("2FA disabled");
    refresh();
  }

  async function copySecret() {
    await navigator.clipboard.writeText(setup.secret);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  }

  return (
    <StaffLayout>
      <h1 className="font-display text-4xl">Security · Two-factor authentication</h1>
      <p className="text-sm text-[#71717A] mt-1 max-w-2xl">
        Protect your admin account with a 6-digit code from Google Authenticator, Authy, 1Password or any TOTP app.
        After enabling, you'll need both your password and a 6-digit code to sign in.
      </p>

      <div className="mt-8 max-w-2xl bg-white border border-[#E4E4E7] rounded-md p-8">
        {status.enabled && !setup && (
          <div data-testid="twofa-enabled-panel">
            <div className="flex items-center gap-3 text-emerald-700">
              <ShieldCheck size={22} />
              <div>
                <div className="font-medium">2FA is enabled on your account</div>
                <div className="text-xs text-[#71717A]">Codes come from your authenticator app.</div>
              </div>
            </div>
            <button data-testid="twofa-disable-btn" onClick={disable} className="mt-6 inline-flex items-center gap-2 border border-rose-300 text-rose-700 px-4 py-2.5 rounded-sm text-sm hover:bg-rose-50">
              <ShieldOff size={14} /> Disable 2FA
            </button>
          </div>
        )}

        {!status.enabled && !setup && (
          <div data-testid="twofa-disabled-panel">
            <div className="flex items-center gap-3 text-[#52525B]">
              <ShieldOff size={22} />
              <div>
                <div className="font-medium">2FA is not enabled</div>
                <div className="text-xs text-[#71717A]">Anyone with your password can sign in.</div>
              </div>
            </div>
            <button data-testid="twofa-start-btn" onClick={startSetup} className="mt-6 inline-flex items-center gap-2 bg-brand-primary text-white px-5 py-2.5 rounded-sm text-sm">
              <KeyRound size={14} /> Set up 2FA
            </button>
          </div>
        )}

        {setup && (
          <div data-testid="twofa-setup-panel">
            <h2 className="font-display text-2xl">Scan the QR code</h2>
            <p className="text-sm text-[#71717A] mt-1">Open Google Authenticator, Authy or 1Password and scan this code. Then enter the 6-digit code shown in the app.</p>
            <div className="mt-6 flex flex-col sm:flex-row gap-6 items-start">
              <img src={setup.qr_data_uri} alt="2FA QR" data-testid="twofa-qr" className="w-48 h-48 border border-[#E4E4E7] rounded-md" />
              <div className="text-sm flex-1">
                <div className="text-xs uppercase tracking-wider text-[#71717A]">Can't scan? Enter this code manually:</div>
                <div className="mt-2 flex items-center gap-2 bg-[#FAFAFA] border border-[#E4E4E7] rounded-sm px-3 py-2 font-mono text-xs">
                  <span data-testid="twofa-secret" className="flex-1 break-all">{setup.secret}</span>
                  <button onClick={copySecret} className="text-brand-primary">{copied ? <Check size={14} /> : <Copy size={14} />}</button>
                </div>
              </div>
            </div>
            <form onSubmit={verifyCode} className="mt-6 flex gap-3 flex-wrap items-end">
              <label className="block flex-1 min-w-[180px]">
                <span className="text-xs uppercase tracking-wider text-[#71717A]">6-digit code</span>
                <input data-testid="twofa-code-input" inputMode="numeric" pattern="[0-9]*" maxLength={6} required value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} placeholder="123456" className="mt-1 w-full border border-[#E4E4E7] rounded-sm px-3 py-2.5 text-base tracking-[0.4em] font-mono" />
              </label>
              <button data-testid="twofa-verify-btn" type="submit" className="bg-brand-primary text-white px-6 py-2.5 rounded-sm text-sm">Verify & enable</button>
              <button type="button" onClick={() => setSetup(null)} className="px-4 py-2.5 text-sm">Cancel</button>
            </form>
          </div>
        )}
      </div>
    </StaffLayout>
  );
}
